import { useState, useCallback } from 'react';
import { validateForm, hasFormErrors } from '../utils/validation';

/**
 * Custom hook for form handling with validation
 * @param {object} initialValues - Initial form values
 * @param {array} requiredFields - Array of required field names
 * @param {function} onSubmit - Function to call on form submission
 * @param {string} validationContext - Validation context ('login', 'signup', etc.)
 * @returns {object} Form state and handlers
 */
const useForm = (initialValues = {}, requiredFields = [], onSubmit = () => {}, validationContext = 'default') => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  
  // Handle input change
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    
    // Handle different input types
    const inputValue = type === 'checkbox' ? checked : value;
    
    setValues(prevValues => ({
      ...prevValues,
      [name]: inputValue
    }));
    
    // Mark field as touched
    if (!touched[name]) {
      setTouched(prevTouched => ({
        ...prevTouched,
        [name]: true
      }));
    }
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, touched]);
  
  // Handle input blur (validate on blur)
  const handleBlur = useCallback((e) => {
    const { name } = e.target;
    
    // Mark field as touched
    setTouched(prevTouched => ({
      ...prevTouched,
      [name]: true
    }));
    
    // Validate single field
    const fieldErrors = validateForm({ [name]: values[name] }, 
      requiredFields.includes(name) ? [name] : [], validationContext);
    
    setErrors(prevErrors => ({
      ...prevErrors,
      ...fieldErrors
    }));
  }, [values, requiredFields, validationContext]);
  
  // Set a specific field value programmatically
  const setFieldValue = useCallback((name, value) => {
    setValues(prevValues => ({
      ...prevValues,
      [name]: value
    }));
  }, []);
  
  // Set multiple field values programmatically
  const setMultipleFields = useCallback((newValues) => {
    setValues(prevValues => ({
      ...prevValues,
      ...newValues
    }));
  }, []);
  
  // Reset form to initial values
  const resetForm = useCallback(() => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setSubmitError('');
  }, [initialValues]);
  
  // Validate all form fields
  const validateAllFields = useCallback(() => {
    const formErrors = validateForm(values, requiredFields, validationContext);
    setErrors(formErrors);
    return !hasFormErrors(formErrors);
  }, [values, requiredFields, validationContext]);
  
  // Handle form submission
  const handleSubmit = useCallback(async (e) => {
    if (e) e.preventDefault();
    
    // Mark all fields as touched
    const allTouched = Object.keys(values).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setTouched(allTouched);
    
    // Validate all fields
    const isValid = validateAllFields();
    
    if (isValid) {
      setIsSubmitting(true);
      setSubmitError('');
      
      try {
        await onSubmit(values);
      } catch (error) {
        setSubmitError(error.message || 'An error occurred during submission');
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [values, validateAllFields, onSubmit]);
  
  return {
    values,
    errors,
    touched,
    isSubmitting,
    submitError,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setMultipleFields,
    resetForm,
    validateAllFields,
  };
};

export default useForm;
